import { ServiceScheduleItem } from '../../client/appTypings';
import { Dispatch, ExtraArguments, Thunk } from '../../client/configureStore';
import { playerViewFetchSuccessAction } from './actions';
import * as AreenaApi from '../../service/areena-api/fetchers';
import { CardResponse, Pointer, ViewResponse } from '@yleisradio/areena-types';
import { logger } from '../../common/logger';
import { isServiceId } from '../../client/utils/item';
import { mediaApi } from '../media/mediaApi';
import { getMediaProtocol } from '../../client/utils/getMediaProtocol';
import { isFakeLiveVod } from '../../client/Player/utils/utils';
import { Mode } from '../../common/types';
import { LoggedInUser } from '../../common/types';
import { getUILanguage } from '../../client/localizations/language';

const getPlaylistSourcePointerThunk = (
  sourceUri: string,
  user: LoggedInUser | null
): Thunk<Promise<Pointer | undefined>> => {
  return async (_dispatch: Dispatch, _, { authFetch }: ExtraArguments) => {
    if (!sourceUri) {
      return undefined;
    }
    const card = await AreenaApi.getNextEpisodeFromPlaylistSource(
      authFetch,
      sourceUri,
      Boolean(user)
    );
    if (card?.pointer) {
      return card.pointer;
    }
    return undefined;
  };
};

export const fetchNextFromPlaylistSourceThunk = (
  playlistSourceUri: string | undefined,
  setNextPointerFromPlaylistSource: (pointer: Pointer | null) => void,
  user: LoggedInUser | null
): Thunk<Promise<void>> => {
  return async dispatch => {
    if (playlistSourceUri) {
      try {
        const pointer = await dispatch(
          getPlaylistSourcePointerThunk(playlistSourceUri, user)
        );
        if (pointer) {
          setNextPointerFromPlaylistSource(pointer);
        } else {
          setNextPointerFromPlaylistSource(null);
        }
      } catch (e) {
        setNextPointerFromPlaylistSource(null);
        logger.error('Failed to fetch next from playlist source:', e);
      }
    } else {
      setNextPointerFromPlaylistSource(null);
    }
  };
};

const getTimeToUpdate = (
  serviceSchedule: ServiceScheduleItem[] | undefined
) => {
  if (serviceSchedule && !isFakeLiveVod(serviceSchedule)) {
    const lastIndex = serviceSchedule.length - 1;

    const lastServiceScheduleItem = serviceSchedule[lastIndex];
    const lastEndTime = lastServiceScheduleItem?.endTime;

    if (lastEndTime) {
      const currentTime = Date.now();
      const lastItemEndTime = Date.parse(lastEndTime);
      const offset = 30000;
      const updateAfterInMs = lastItemEndTime - currentTime - offset;
      return Math.max(0, updateAfterInMs);
    }
  }
};

export const getPlayerThunk =
  (
    mode: Mode,
    viewId: string,
    itemId: string,
    programId?: string,
    publicationId?: string
  ): Thunk<Promise<void>> =>
  async (dispatch, getState, { authFetch, appFeatures, serverCookies }) => {
    const {
      currentAddress: { countryCode },
    } = getState();
    const isSimulcastLive = isServiceId(itemId);

    let playerViewResponse: ViewResponse | undefined;

    if (!authFetch) throw new Error('no authFetch');

    if (isSimulcastLive) {
      playerViewResponse = await AreenaApi.getLivePlayerView(
        authFetch,
        serverCookies,
        itemId,
        programId,
        publicationId
      );
      dispatch(
        playerViewFetchSuccessAction({
          viewId,
          view: playerViewResponse,
        })
      );
    } else {
      playerViewResponse = await AreenaApi.getPlayerView(
        authFetch,
        itemId,
        serverCookies
      );
      dispatch(
        playerViewFetchSuccessAction({
          viewId: itemId,
          view: playerViewResponse,
        })
      );
    }

    const { id: mediaId, type: mediaType } =
      playerViewResponse?.data?.header?.player?.media ||
      playerViewResponse?.data?.players?.live?.media ||
      {};

    const idToRequestMediaWith = isSimulcastLive ? itemId : mediaId;

    if (mediaType && idToRequestMediaWith) {
      const protocol = getMediaProtocol(mode, mediaType, idToRequestMediaWith);

      const query = dispatch(
        mediaApi.endpoints.getMedia.initiate(
          {
            languageCode: getUILanguage(),
            countryCode,
            mode,
            id: idToRequestMediaWith,
            device: appFeatures.device,
          },
          { subscriptionOptions: { pollingInterval: 0 } }
        )
      );
      const { data } = await query;
      const mediaData = data?.[protocol];
      const mediaItem = Array.isArray(mediaData) ? mediaData[0] : mediaData;

      if (mediaItem?.serviceSchedule) {
        const interval = getTimeToUpdate(mediaItem.serviceSchedule);
        query.updateSubscriptionOptions({
          pollingInterval: interval,
        });
      }
    }
  };

export const emptyList: CardResponse = {
  data: [],
  meta: { count: 0, offset: 0, limit: 0 },
};
